/**
 * Валидация доп. поля типа операции на клиенте (AK-527).
 *
 * Зеркало серверного `validate_fields_schema`
 * (`backend/src/ikolhoz/api/v1/reference/_helpers.py`). Сервер остаётся
 * источником правды — здесь ровно те же правила, но проверенные ДО запроса,
 * чтобы пользователь видел «ключ уже есть» сразу под полем, а не получал
 * 409/422 после сохранения.
 */
import type { ExtraFieldDef } from '@/lib/reference/companyOperationTypes';

/** Типы значений доп. поля (ALLOWED_FIELD_TYPES на бэке). */
export const FIELD_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'number', label: 'Число' },
  { value: 'string', label: 'Текст' },
  { value: 'enum', label: 'Список значений' },
  { value: 'bool', label: 'Да / нет' },
  { value: 'date', label: 'Дата' },
];

export interface ExtraFieldDraft {
  key: string;
  type: string;
  label: string;
  unit: string;
  required: boolean;
  options: string;
}

export const EMPTY_DRAFT: ExtraFieldDraft = {
  key: '',
  type: 'number',
  label: '',
  unit: '',
  required: false,
  options: '',
};

/**
 * Ключ — латиница/цифры/подчёркивание: он уезжает в `attrs` jsonb и в выгрузки
 * во внешние системы, где кириллица и пробелы ломают потребителей.
 */
const KEY_RE = /^[a-z][a-z0-9_]*$/;

export function validateDraft(
  draft: ExtraFieldDraft,
  existingKeys: string[],
): string | null {
  const key = draft.key.trim();
  if (!key) return 'Укажите ключ поля.';
  if (!KEY_RE.test(key)) {
    return 'Ключ — латиница в нижнем регистре, цифры и «_», начинается с буквы.';
  }
  if (existingKeys.includes(key)) return `Поле с ключом «${key}» уже есть.`;
  if (!draft.label.trim()) return 'Укажите название поля.';
  if (!FIELD_TYPES.some((t) => t.value === draft.type)) return 'Выберите тип поля.';
  if (draft.type === 'enum' && parseOptions(draft.options).length === 0) {
    return 'Для списка значений перечислите варианты через запятую.';
  }
  return null;
}

export function parseOptions(raw: string): string[] {
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return Array.from(new Set(parts));
}

/** Черновик → тело запроса (без пустых опциональных ключей). */
export function draftToFieldDef(draft: ExtraFieldDraft): ExtraFieldDef {
  const def: ExtraFieldDef = {
    key: draft.key.trim(),
    type: draft.type,
    label: draft.label.trim(),
  };
  if (draft.unit.trim()) def.unit = draft.unit.trim();
  if (draft.required) def.required = true;
  if (draft.type === 'enum') def.options = parseOptions(draft.options);
  return def;
}

/** Человеческое описание поля для списка: «Глубина, см · число · обязательное». */
export function fieldSummary(f: ExtraFieldDef): string {
  const type = FIELD_TYPES.find((t) => t.value === f.type)?.label ?? f.type;
  return [type, f.unit || null, f.required ? 'обязательное' : null]
    .filter(Boolean)
    .join(' · ');
}
